import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import Button from '@common/button';
import Text from '@common/text';
import Icon from '@components/common/assets';
import { useToast } from '@components/common/toast';
import Modal from '@components/common/modal';
import { useVerificationLongTermInActiveCheck } from '@repository/verification/useMobile';
import { useRenewAccessTokenRefreshToken, useUserInfo } from '@repository/auth/useUserInfo';
import Step1 from './step1';
import Step2 from './step2';
import { FontStyle } from '@domain/constant/fontStyle';
import Colors from '@domain/constant/colors';
import { JOBDA_SUPPORT_EMAIL } from '@domain/constant/contact';
import useModals from '@utils/hooks/useModals';
import { RouteUrl } from '@utils/routeUtil';
import useKcbIdentity from '@utils/hooks/useKcbIdentity';
import { hasErrorMessage } from '@utils/typeGuard';
import classNames from 'classnames/bind';
import styles from './index.module.scss';

const cx = classNames.bind(styles);

const UpdateAccount = () => {
  const Toast = useToast();
  const router = useRouter();
  const { data: userInfo } = useUserInfo();
  const { openModal, isVisible, closeModal, modalState } = useModals(['step1', 'step2']);
  const openStep1 = () => openModal('step1');
  const openStep2 = () => openModal('step2');
  const onConfirm = () => router.push(RouteUrl.INDEX);

  const ModalClose = () => {
    if (modalState === 'step1') {
      closeModal();
    }
    if (modalState === 'step2') {
      openModal('step1');
    }
  };

  const { mutateAsync: updateVerificationLongTermInActiveCheck } = useVerificationLongTermInActiveCheck({
    onError: (e) => {
      if (hasErrorMessage(e)) Toast({ type: 'error', iconType: 'info', content: e.response.data.message });
    },
  });
  const { mutateAsync: renewToken } = useRenewAccessTokenRefreshToken();

  useKcbIdentity({
    onSuccess: async () => {
      await updateVerificationLongTermInActiveCheck(undefined);
      await renewToken();
      onConfirm();
    },
  });

  useEffect(() => {
    if (!userInfo?.authorities.includes('LONG_TERM_INACTIVE_VERIFICATION_REQUIRED')) router.replace(RouteUrl.INDEX);
  }, [userInfo, router]);

  if (!userInfo) return null;

  return (
    <>
      <div className={cx('area')}>
        <div className={cx('wrap')}>
          <div className={cx('logoArea')}>
            <Icon name="logo" width={118} height={32} />
          </div>

          <div className={cx('contentsArea')}>
            <Text fontStyle={FontStyle.H4_B} color={Colors.C_JOBDA_BLACK}>
              안녕하세요!
              <br />
              JOBDA에 오랜만에 방문하셨네요.
            </Text>

            <Text fontStyle={FontStyle.B2P_M} color={Colors.C_COOL_GRAY_70}>
              먼저, 잡다를 이용해 주셔서 감사합니다.
              <br />
              저장된 개인정보 보호법에 따라 잡다의 운영정책도 아래와 같이 변경되어 안내드립니다.
              <div className={cx('space')} />
              <em className={cx('emphasis')}>[내용]</em>
              개인정보보호법 개정(제39조의 6, 개인정보 유효기간제 폐지)에 따라 잡다 휴면계정 정책 폐지
              <li>{`「개인정보 처리방침」'개인정보 유효기간' 정책 삭제`}</li>
              <li>「서비스 이용약관」제18 조(계약해제, 해지, 휴면 등) 휴면 정책 삭제</li>
              <li>「서비스 이용약관」제19 조(이용제한 등) 장기 미이용자에 대한 정책 삭제</li>
              <div className={cx('space')} />
              <em className={cx('emphasis')}>[변경일자]</em>
              2024년 1월 31일
              <div className={cx('space')} />
              <em className={cx('emphasis')}>[안내사항]</em>
              {`① 마케팅 수신 동의 내역은 동일하게 유지되며, 변경을 원하시는 경우 ‘계정 설정 > 알림 관리’에서 변경하`}
              <br />
              실 수 있습니다.
              <br />② 궁금하신 사항이 있으신 경우{' '}
              <a href={`mailto:${JOBDA_SUPPORT_EMAIL}`} className={cx('link')} role="button">
                대표 메일
              </a>
              로 문의하시면 안내 드리겠습니다.
              <br />
              ③ 오랜만에 방문한 회원분들의 개인정보를 안전히 지키기 위해 ‘본인인증’ 후 서비스를 제공하고 있습니다.
              <br />* 내 정보에 등록된 내용이 오래전 연락처인 경우에는{' '}
              <a href={`mailto:${JOBDA_SUPPORT_EMAIL}`} className={cx('link')} role="button">
                대표 메일
              </a>
              로 문의해 주세요.
            </Text>
          </div>

          <Button className={cx('btn')} onClick={openStep1}>
            본인 인증하고 잡다 시작하기
          </Button>
        </div>
      </div>

      <Modal visible={modalState !== null}>
        <div className={cx('contents')}>
          <button className={cx('closeBtn')} onClick={ModalClose}>
            <Icon name="closeLight" width={32} height={32} />
          </button>
          <Icon name="logo" width={118} height={32} />
          {isVisible('step1') && <Step1 onNext={openStep2} />}
          {isVisible('step2') && <Step2 onConfirm={onConfirm} />}
        </div>
      </Modal>
    </>
  );
};

export default UpdateAccount;
